import { create } from 'zustand';
import type { CustomerGroupDto, CustomerGroupInput, CustomerGroupRules } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Customer groups by rules (ADR-085) — searched and paged on the server; added,
 * changed, refreshed and deleted here; a rule set's reach previewed as typed.
 */

interface CustomerGroupsState {
  items: CustomerGroupDto[];
  nextCursor: string | null;
  total: number | null;
  search: string;
  loading: boolean;
  error: string | null;
  saving: boolean;
  saveError: string | null;
  previewCount: number | null;
  /** The groups the open customer is in. */
  memberOf: { customerId: string; groups: CustomerGroupDto[] } | null;

  load: (filters?: { search?: string }) => Promise<void>;
  loadMore: () => Promise<void>;
  save: (id: string | null, input: CustomerGroupInput) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
  refresh: (id: string) => Promise<void>;
  preview: (rules: CustomerGroupRules) => Promise<void>;
  clearPreview: () => void;
  loadMemberOf: (customerId: string) => Promise<void>;
}

const PAGE_SIZE = 20;

export const useCustomerGroupsStore = create<CustomerGroupsState>((set, get) => ({
  items: [],
  nextCursor: null,
  total: null,
  search: '',
  loading: false,
  error: null,
  saving: false,
  saveError: null,
  previewCount: null,
  memberOf: null,

  async load(filters = {}) {
    const search = filters.search ?? get().search;
    set({ loading: true, error: null, search });
    try {
      const page = await withSession(() => adminApi.customerGroups.list({ limit: PAGE_SIZE, ...(search.trim() ? { search: search.trim() } : {}) }));
      if (get().search !== search) return;
      set({ items: page.items, nextCursor: page.meta.nextCursor, total: page.meta.total ?? null, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the groups.') });
    }
  },

  async loadMore() {
    const { nextCursor, search, loading } = get();
    if (!nextCursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() =>
        adminApi.customerGroups.list({ limit: PAGE_SIZE, cursor: nextCursor, ...(search.trim() ? { search: search.trim() } : {}) }),
      );
      set((state) => ({ items: [...state.items, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more groups.') });
    }
  },

  async save(id, input) {
    set({ saving: true, saveError: null });
    try {
      const saved = await withSession(() => (id ? adminApi.customerGroups.update(id, input) : adminApi.customerGroups.create(input)));
      set((state) => ({
        saving: false,
        items: id ? state.items.map((item) => (item.id === id ? saved : item)) : [...state.items, saved],
        total: id || state.total === null ? state.total : state.total + 1,
      }));
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save the group.') });
      return false;
    }
  },

  async remove(id) {
    set({ saving: true, saveError: null });
    try {
      await withSession(() => adminApi.customerGroups.remove(id));
      set((state) => ({ saving: false, items: state.items.filter((item) => item.id !== id), total: state.total === null ? null : state.total - 1 }));
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not delete the group.') });
      return false;
    }
  },

  async refresh(id) {
    set({ saving: true, error: null });
    try {
      const fresh = await withSession(() => adminApi.customerGroups.refresh(id));
      set((state) => ({ saving: false, items: state.items.map((item) => (item.id === id ? fresh : item)) }));
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not refresh the group.') });
    }
  },

  async preview(rules) {
    try {
      const result = await withSession(() => adminApi.customerGroups.preview(rules));
      set({ previewCount: result.count });
    } catch {
      // A preview that fails says nothing rather than something wrong.
      set({ previewCount: null });
    }
  },

  clearPreview() {
    set({ previewCount: null });
  },

  async loadMemberOf(customerId) {
    try {
      const page = await withSession(() => adminApi.customerGroups.ofCustomer(customerId));
      set({ memberOf: { customerId, groups: page.items } });
    } catch {
      // Groups are an aside on the customer's page: a failed read hides them.
      set({ memberOf: { customerId, groups: [] } });
    }
  },
}));
